import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { sanitizeForFirestore } from "./firebaseSafe";
import {
  CONTACT_EMAIL,
  DEFAULT_PRICING,
  STUDIO_MAX_HOURS,
  isValidEmail,
  mergePricing,
  slotDocId,
} from "./platformData";

export async function ensureUserProfile(user, extra = {}) {
  if (!user) return null;
  const profile = {
    uid: user.uid,
    email: user.email || "",
    displayName: user.displayName || extra.displayName || "",
    photoURL: user.photoURL || "",
    updatedAt: serverTimestamp(),
    ...extra,
  };

  await setDoc(
    doc(db, "users", user.uid),
    sanitizeForFirestore({ ...profile, createdAt: extra.createdAt || serverTimestamp() }),
    { merge: true }
  );

  return profile;
}

export async function getPricing() {
  const snap = await getDoc(doc(db, "settings", "pricing"));
  return mergePricing(snap.exists() ? snap.data() : DEFAULT_PRICING);
}

export async function savePricing(pricing) {
  await setDoc(
    doc(db, "settings", "pricing"),
    sanitizeForFirestore({ ...mergePricing(pricing), updatedAt: serverTimestamp() }),
    { merge: true }
  );
}

export async function createStudioBooking(user, item, pricing) {
  if (!user) throw new Error("Connexion obligatoire pour reserver.");
  const contentSnap = await getDoc(doc(db, "admin", "content"));
  const studioSettings = contentSnap.exists() ? contentSnap.data()?.pages?.studio || {} : {};
  if (studioSettings.bookingEnabled === false) {
    throw new Error(
      studioSettings.privateMessage ||
        "Le studio ne peut pas être réservé pour le moment, car il s'agit d'un studio privé."
    );
  }
  if (!item?.date || !Array.isArray(item.hours) || item.hours.length === 0) {
    throw new Error("Choisis une date et au moins un creneau.");
  }
  if (item.hours.length > STUDIO_MAX_HOURS) {
    throw new Error(`Maximum ${STUDIO_MAX_HOURS}h par reservation.`);
  }

  const hourlyRate = Number(
    item.sessionType === "without_engineer"
      ? pricing?.studioWithoutEngineer ?? 25
      : pricing?.studioWithEngineer ?? pricing?.studioHourly ?? 40
  );
  const hours = [...new Set(item.hours)].sort();
  const slotIds = hours.map((hour) => slotDocId(item.date, hour));
  const expiresAt = Timestamp.fromDate(new Date(Date.now() + 30 * 60 * 1000));

  return runTransaction(db, async (transaction) => {
    const slotRefs = slotIds.map((id) => doc(db, "studioSlots", id));
    const slotSnaps = [];

    for (const ref of slotRefs) {
      slotSnaps.push(await transaction.get(ref));
    }

    const blocked = slotSnaps.find((snap) => {
      if (!snap.exists()) return false;
      const status = snap.data()?.status;
      return ["pending", "confirmed", "blocked"].includes(status);
    });

    if (blocked) {
      throw new Error("Un ou plusieurs creneaux viennent d'etre pris.");
    }

    const bookingRef = doc(collection(db, "studioBookings"));
    const total = hourlyRate * hours.length;
    const payload = {
      id: bookingRef.id,
      userId: user.uid,
      userEmail: user.email || "",
      userName: user.displayName || "",
      date: item.date,
      hours,
      slotIds,
      sessionType: item.sessionType || "with_engineer",
      sessionLabel: item.sessionLabel || "Studio avec ingénieur",
      startTime: item.startTime || hours[0],
      endTime: item.endTime || "",
      duration: Number(item.duration || hours.length),
      hourlyRate,
      total,
      currency: pricing?.currency || "EUR",
      status: "pending",
      paymentStatus: "admin_validation_required",
      paymentEnabled: studioSettings.paymentEnabled === true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    transaction.set(bookingRef, sanitizeForFirestore(payload));
    slotRefs.forEach((ref, index) => {
      transaction.set(ref, sanitizeForFirestore({
        date: item.date,
        hour: hours[index],
        bookingId: bookingRef.id,
        userId: user.uid,
        status: "pending",
        expiresAt,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }));
    });

    return bookingRef.id;
  });
}

export async function updateStudioBookingStatus(booking, status) {
  if (!booking?.id) return;
  const batch = writeBatch(db);
  const bookingRef = doc(db, "studioBookings", booking.id);

  batch.update(bookingRef, sanitizeForFirestore({
    status,
    updatedAt: serverTimestamp(),
  }));

  (booking.slotIds || []).forEach((slotId) => {
    const slotRef = doc(db, "studioSlots", slotId);
    if (["refused", "cancelled"].includes(status)) {
      batch.delete(slotRef);
    } else {
      batch.set(
        slotRef,
        sanitizeForFirestore({
          status,
          bookingId: booking.id,
          date: booking.date,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
    }
  });

  await batch.commit();
}

export async function blockStudioSlots({ date, hours, reason = "" }) {
  if (!date || !Array.isArray(hours) || !hours.length) {
    throw new Error("Date et creneaux requis.");
  }

  const blockRef = doc(collection(db, "studioBlockedSlots"));
  const slotIds = hours.map((hour) => slotDocId(date, hour));
  const slotRefs = slotIds.map((id) => doc(db, "studioSlots", id));

  await runTransaction(db, async (transaction) => {
    const existing = await Promise.all(slotRefs.map((ref) => transaction.get(ref)));
    if (existing.some((snap) => snap.exists())) {
      throw new Error("Un ou plusieurs créneaux sont déjà pris ou bloqués.");
    }

    transaction.set(blockRef, sanitizeForFirestore({
      id: blockRef.id,
      date,
      hours,
      slotIds,
      reason,
      createdAt: serverTimestamp(),
    }));

    slotRefs.forEach((ref, index) => {
      transaction.set(ref, sanitizeForFirestore({
        date,
        hour: hours[index],
        status: "blocked",
        blockId: blockRef.id,
        reason,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }));
    });
  });
}

export async function unblockStudioSlot(slotId) {
  await deleteDoc(doc(db, "studioSlots", slotId));
}

export async function createServiceOrder(user, item) {
  if (!user) throw new Error("Connexion obligatoire pour commander.");
  const total = Number(item.unitPrice || 0) * Number(item.quantity || 1);
  const ref = await addDoc(collection(db, "serviceOrders"), sanitizeForFirestore({
    userId: user.uid,
    userEmail: user.email || "",
    userName: user.displayName || "",
    serviceId: item.serviceId,
    serviceName: item.name,
    files: item.files || [],
    quantity: item.quantity || 1,
    unitPrice: Number(item.unitPrice || 0),
    total,
    currency: item.currency || "EUR",
    status: "pending",
    paymentStatus: "pending_payment",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));
  return ref.id;
}

export async function createLatdiorOrder(user, items) {
  if (!user) throw new Error("Connexion obligatoire pour commander.");
  const orderItems = items.map((item) => ({
    productId: item.productId,
    name: item.name,
    size: item.size || "",
    image: item.image || "",
    quantity: Number(item.quantity || 1),
    unitPrice: Number(item.unitPrice || 0),
    total: Number(item.unitPrice || 0) * Number(item.quantity || 1),
  }));
  const total = orderItems.reduce((sum, item) => sum + item.total, 0);
  const ref = await addDoc(collection(db, "latdiorOrders"), sanitizeForFirestore({
    userId: user.uid,
    userEmail: user.email || "",
    userName: user.displayName || "",
    items: orderItems,
    total,
    currency: "EUR",
    status: "pending",
    paymentStatus: "pending_payment",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));
  return ref.id;
}

export async function createNewsletterSubscription(email, source = "site") {
  const normalized = String(email || "").trim().toLowerCase();
  if (!isValidEmail(normalized)) {
    throw new Error("Email invalide.");
  }

  const id = normalized.replace(/[^a-z0-9._-]/g, "_");
  await setDoc(
    doc(db, "newsletterSubscribers", id),
    sanitizeForFirestore({
      email: normalized,
      source,
      status: "active",
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    }),
    { merge: true }
  );
}

export async function createContactMessage(payload) {
  if (payload.website) {
    throw new Error("Message bloque.");
  }
  if (!payload.name || !isValidEmail(payload.email) || !payload.message) {
    throw new Error("Complete le nom, l'email et le message.");
  }

  const ref = await addDoc(collection(db, "contactMessages"), sanitizeForFirestore({
    name: String(payload.name).trim(),
    email: String(payload.email).trim().toLowerCase(),
    subject: String(payload.subject || "Contact Unlimited Session").trim(),
    message: String(payload.message).trim(),
    sentTo: CONTACT_EMAIL,
    status: "new",
    emailSent: false,
    emailQueued: true,
    createdAt: serverTimestamp(),
  }));

  let emailSent = false;
  try {
    const [{ getFunctions, httpsCallable }, { default: app }] = await Promise.all([
      import("firebase/functions"),
      import("../firebase"),
    ]);
    const sendContactEmail = httpsCallable(getFunctions(app, "europe-west1"), "sendContactEmail");
    await sendContactEmail({ messageId: ref.id });
    emailSent = true;
  } catch {
    // Le message reste visible dans l'admin si la fonction email n'est pas encore deployee.
  }

  return { id: ref.id, emailSent };
}

export async function createWaitlistRequest({ email, user, productId, productName }) {
  const normalized = String(email || user?.email || "").trim().toLowerCase();
  if (!isValidEmail(normalized)) throw new Error("Email invalide.");
  const cleanProductId = String(productId || "").trim();
  if (!cleanProductId) throw new Error("Produit introuvable.");

  const keyBytes = new TextEncoder().encode(`${cleanProductId}|${normalized}`);
  const digest = await window.crypto.subtle.digest("SHA-256", keyBytes);
  const requestId = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 40);
  const requestRef = doc(db, "waitlistRequests", requestId);

  try {
    await setDoc(requestRef, sanitizeForFirestore({
      email: normalized,
      userId: user?.uid || "",
      productId: cleanProductId,
      productName: String(productName || "Produit Latdior").trim(),
      status: "waiting",
      emailSent: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
  } catch (error) {
    if (error?.code === "permission-denied") {
      return { id: requestId, emailSent: false, alreadyRegistered: true };
    }
    throw error;
  }

  // La fonction Firestore waitlistRequestCreated envoie une confirmation unique.
  return { id: requestId, emailSent: false, alreadyRegistered: false };
}

export async function retryWaitlistConfirmation(requestId) {
  if (!requestId) throw new Error("Demande introuvable.");
  const [{ getFunctions, httpsCallable }, { default: app }] = await Promise.all([
    import("firebase/functions"),
    import("../firebase"),
  ]);
  const sendWaitlistConfirmation = httpsCallable(
    getFunctions(app, "europe-west1"),
    "sendWaitlistConfirmation"
  );
  const result = await sendWaitlistConfirmation({ requestId });
  return result.data || { emailSent: false };
}

export async function updateDocument(collectionName, id, patch) {
  await updateDoc(doc(db, collectionName, id), sanitizeForFirestore({
    ...patch,
    updatedAt: serverTimestamp(),
  }));
}
